import type { ScopingType } from '../types/reportBuilder';

export const SCOPING_LABELS: Record<ScopingType, string> = {
  client_admin: 'All Data (Admin)',
  global_hr: 'All Employees',
  scoped_hr: 'My HR Scope',
  executive: 'Executive View',
  manager: 'My Team',
  self: 'My Data', // Not offered in report builder (see SCOPE_HIERARCHY)
};

export const SCOPING_DESCRIPTIONS: Record<ScopingType, string> = {
  client_admin: 'Full access to all organizational data across every department and team.',
  global_hr: 'Access to all employee data organization-wide for HR reporting.',
  scoped_hr: 'Access to employee data within your assigned HR scope.',
  executive: 'Access to data for all teams under your executive purview.',
  manager: 'Access to data for your direct reports and their teams.',
  self: 'Access to your own personal data only.',
};

/** Ordered from highest to lowest privilege. 'self' is excluded — report builder is not available for personal-only scope. */
const SCOPE_HIERARCHY: ScopingType[] = [
  'client_admin',
  'global_hr',
  'scoped_hr',
  'executive',
  'manager',
];

interface AccessFlags {
  isSuperAdmin: boolean;
  isGlobalHRBP: boolean;
  isScopedHRBP: boolean;
  isExecutive: boolean;
  isManager: boolean;
}

/**
 * Returns the scopes a user is authorized to use, ordered highest-privilege first.
 * Higher roles cumulatively include all lower scopes.
 */
export function getAuthorizedScopes(access: AccessFlags): ScopingType[] {
  let startIndex: number;

  if (access.isSuperAdmin) {
    startIndex = 0; // client_admin and below
  } else if (access.isGlobalHRBP) {
    startIndex = 1; // global_hr and below
  } else if (access.isScopedHRBP) {
    startIndex = 2; // scoped_hr and below
  } else if (access.isExecutive) {
    startIndex = 3; // executive and below
  } else if (access.isManager) {
    startIndex = 4; // manager only
  } else {
    return []; // no report builder scoping for employee-only users
  }

  return SCOPE_HIERARCHY.slice(startIndex);
}

/**
 * Returns the highest-privilege scope available to the user (for auto-selection).
 */
export function getDefaultScope(access: AccessFlags): ScopingType {
  return getAuthorizedScopes(access)[0];
}
